import { useState } from 'react';
import { IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tooltipSx } from '../../components/shared/jernyTooltipStyling';

// AccountTab-only code snippet display component.
// Copy button sits inside the Paper top-right via a relative/absolute wrapper.
// Label uses SettingsRow font spec for visual consistency with the rest of AccountTab.
// Do NOT use outside AccountTab — use CopyBlock for shared usage.

const J = {
    surface: '#1a2a40',
    border: '0.5px solid #294766',
    muted: '#a3d1ff',
    accent: '#66b2ff',
};

const highlightJson = (json) => {
    return json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"([^"]+)"(\s*:)?/g, (m, val, colon) => {
            // Keys — white + bold
            if (colon) return `<span style="color:#e0f0ff;font-weight:600">"${val}"</span>${colon}`;
            // String values — green
            return `<span style="color:#4cceac">"${val}"</span>`;
        })
        .replace(/(?<![-\w]):\s*(\d+)(?!\w)/g, ': <span style="color:#f0b429">$1</span>')
        .replace(/[{}\[\]]/g, (m) => `<span style="color:#a3d1ff">${m}</span>`);
};

/**
 * SnippetCopy — code snippet block with copy button inside the Paper, top-right.
 * @param {string} label - Subtext shown below the Paper, consistent with SettingsRow label font.
 * @param {string} value - The text content to display.
 * @param {string} [copyValue] - The text to copy — defaults to value if not provided.
 * @param {boolean} highlight - If true, applies JSON syntax highlighting.
 */
export default function SnippetCopy({ label, value, copyValue, highlight = false }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        // Copy copyValue if provided, otherwise fall back to display value
        navigator.clipboard.writeText(copyValue ?? value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        // paddingRight creates breathing room from SettingsSection border
        <div style={{ width: '100%', paddingRight: 2, boxSizing: 'border-box', marginBottom: 4 }}>
            <div
                style={{
                    position: 'relative',
                    backgroundColor: J.surface,
                    border: J.border,
                    borderRadius: 4,
                    overflow: 'hidden',
                }}
            >
                {/* Transparent overlay — same text, color transparent, sits on top for selection */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        padding: '12px 12px 12px 12px',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        color: 'transparent',
                        cursor: 'text',
                        zIndex: 2,
                        userSelect: 'none',
                    }}
                >
                    {value}
                </div>
                <div style={{ position: 'relative', padding: '12px 12px 12px 12px' }}>
                    {/* Copy button — anchored top-right inside Paper */}
                    <Tooltip title={copied ? 'Copied!' : 'Copy'} componentsProps={tooltipSx}>
                        <IconButton
                            onClick={handleCopy}
                            size="small"
                            disableRipple
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                zIndex: 9999,
                                border: 'none',
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'transparent' },
                            }}
                        >
                            <ContentCopyIcon fontSize="small" sx={{ color: copied ? '#4cceac' : J.accent }} />
                        </IconButton>
                    </Tooltip>

                    {/* Highlighted content — visual only */}
                    {highlight ? (
                        <div
                            style={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                color: J.muted,
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                MozUserSelect: 'text',
                                msUserSelect: 'text',
                            }}
                            dangerouslySetInnerHTML={{ __html: highlightJson(value) }}
                        />
                    ) : (
                        <div
                            style={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                color: J.muted,
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                MozUserSelect: 'text',
                                msUserSelect: 'text',
                            }}
                        >
                            {value}
                        </div>
                    )}
                </div>
            </div>

            {/* Label — SettingsRow font spec for consistency with AccountTab rows */}
            {label && (
                <Typography
                    sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#b0c8e0',
                        mt: 0.75,
                        WebkitFontSmoothing: 'antialiased',
                    }}
                >
                    {label}
                </Typography>
            )}
        </div>
    );
}
